import { useSearchPatients } from "@elation/api-client/src/stitch-web";
import { SearchPatientsSortBy } from "@elation/api-client/src/stitch-web/models/searchPatientsSortBy";
import { useAppBusEvent, usePatientSyncJob } from "@src/features/sse";
import { navigation } from "@src/navigation";
import {
  PatientsPage,
  type PatientSearchFormValues,
} from "@src/pages/Patients";
import { useActor } from "@src/providers/actorProvider";
import { NaiveDate } from "@src/utils/types";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

function httpStatus(err: unknown): number | undefined {
  const status = (err as { response?: { status?: unknown } } | null)?.response
    ?.status;
  return typeof status === "number" ? status : undefined;
}

export function meta() {
  return [
    { title: "Patients" },
    { name: "description", content: "Patients page" },
  ];
}

type FilterState = {
  q?: string;
  dob?: string;
  zip?: string;
};

type SortState = {
  sortBy: SearchPatientsSortBy;
  order: "asc" | "desc";
};

const DEFAULT_SORT: SortState = { sortBy: "lastName", order: "asc" };

const PAGE_SIZE = 10;

function formValuesToFilters(values: PatientSearchFormValues): FilterState {
  const dob = values.date_of_birth
    ? NaiveDate.toString(values.date_of_birth)
    : undefined;
  return {
    q: values.q?.trim() || undefined,
    dob,
    zip: values.zip_code || undefined,
  };
}

export default function Patients() {
  const navigate = useNavigate();
  const actor = useActor();

  const { patientSyncJob } = usePatientSyncJob({
    enabled: actor.type === "physician",
    initialPatientSyncJobId:
      actor.type === "physician" ? actor.patientSyncJobId : null,
  });

  const [filters, setFilters] = useState<FilterState>({});
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  // Stack of cursors visited. Top of stack = cursor for the CURRENT page.
  // Empty stack = first page. Advancing pushes the response's nextCursor.
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const currentCursor = cursorStack[cursorStack.length - 1];

  const {
    data: patientsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useSearchPatients(
    { ...filters, ...sort, limit: PAGE_SIZE, cursor: currentCursor },
    {
      query: {
        refetchInterval:
          patientSyncJob?.status === "running" ? 2000 : undefined,
        // 4xx responses are caller errors (bad cursor, bad filter); retrying
        // just replays the same bad request. Only retry on 5xx/network.
        retry: (failureCount, err) => {
          const status = httpStatus(err);
          if (status != null && status >= 400 && status < 500) return false;
          return failureCount < 2;
        },
      },
    },
  );

  const patients = patientsData?.data?.items ?? [];
  const nextCursor = patientsData?.data?.nextCursor ?? null;

  // Auto-recover from a stale cursor (e.g. row deleted, tampered URL). The
  // backend signals this with HTTP 400 and we silently reset to the first
  // page; any other error is surfaced to the user by PatientsPage.
  const isStaleCursor =
    isError && currentCursor != null && httpStatus(error) === 400;

  // A cursor is only meaningful under the filter + sort it was minted for
  // (the server binds cursors to sortBy and would 400 on mismatch). Reset
  // the stack on any filter or sort change so Prev doesn't walk stale pages.
  const handleSearch = (values: PatientSearchFormValues) => {
    setFilters(formValuesToFilters(values));
    setCursorStack([]);
  };

  const handleSortChange = (
    sortBy: SearchPatientsSortBy,
    order: "asc" | "desc",
  ) => {
    setSort({ sortBy, order });
    setCursorStack([]);
  };

  const handleNextPage = () => {
    if (nextCursor) setCursorStack((stack) => [...stack, nextCursor]);
  };

  const handlePrevPage = () => {
    setCursorStack((stack) => stack.slice(0, -1));
  };

  const handlePatientSelect = async (patientId: number) => {
    navigate(navigation.patientDetail(String(patientId)));
  };

  const patientSyncProgress = useMemo(() => {
    if (!patientSyncJob) return null;
    const { progress, status } = patientSyncJob;
    switch (status) {
      case "completed":
        return null;
      case "pending":
        return 0;
      case "running": {
        if (progress.total === 0) return 0;
        return Math.min(
          100,
          Math.round((progress.completed / progress.total) * 100),
        );
      }
      default:
        return null;
    }
  }, [patientSyncJob]);

  useEffect(() => {
    if (isStaleCursor) setCursorStack([]);
  }, [isStaleCursor]);

  useAppBusEvent(
    "physician.patient_sync_succeeded",
    async ({ physicianId }) => {
      if (actor.type !== "physician" || actor.user.id !== physicianId) {
        return;
      }

      await refetch();
    },
  );

  return (
    <PatientsPage
      patients={patients}
      isLoading={isLoading}
      isError={isError && !isStaleCursor}
      onPatientSelect={handlePatientSelect}
      onSearch={handleSearch}
      sortBy={sort.sortBy}
      order={sort.order}
      onSortChange={handleSortChange}
      hasNextPage={Boolean(nextCursor)}
      hasPrevPage={cursorStack.length > 0}
      onNextPage={handleNextPage}
      onPrevPage={handlePrevPage}
      patientSyncProgress={patientSyncProgress}
      patientSyncPhase={patientSyncJob?.phase ?? null}
    />
  );
}
